import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { formatApiErrorDetail } from "@/lib/api";
import { AdminLeads } from "@/components/admin/AdminLeads";
import { AdminQueries } from "@/components/admin/AdminQueries";
import { AdminBlogs } from "@/components/admin/AdminBlogs";
import { AdminFaqs } from "@/components/admin/AdminFaqs";
import { AdminQuotes } from "@/components/admin/AdminQuotes";
import { LogOut, Lock } from "lucide-react";

const TABS = ["Leads", "Queries", "Journal", "FAQs", "Quotes"];

export default function Admin() {
  const { user, login, logout } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("Leads");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back, Ayush.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  if (user === null) return <div className="pt-40 text-center font-mono text-sm text-muted-foreground">Loading…</div>;

  if (!user) {
    return (
      <div className="flex min-h-screen items-center justify-center px-6 pt-20">
        <form onSubmit={submit} className="w-full max-w-md border border-border bg-card p-8 md:p-10" data-testid="admin-login">
          <Lock className="mb-6 h-6 w-6 text-cyan" />
          <h1 className="font-display text-3xl font-extrabold uppercase text-foreground">Admin Access</h1>
          <p className="mb-8 mt-2 text-sm text-muted-foreground">Sign in to manage leads, queries and content.</p>
          <div className="space-y-4">
            <Input required type="email" data-testid="admin-email" className="tool-input rounded-none" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} />
            <Input required type="password" data-testid="admin-password" className="tool-input rounded-none" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} />
            <Button type="submit" disabled={loading} data-testid="admin-login-btn" className="w-full rounded-none bg-cyan py-6 font-mono text-xs uppercase tracking-widest text-white hover:bg-cyan/90">
              {loading ? "Signing in…" : "Sign In"}
            </Button>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div className="pt-28">
      <div className="container-px mx-auto max-w-[1600px] pb-24">
        <div className="flex items-center justify-between">
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-cyan">Dashboard</p>
            <h1 className="mt-2 font-display text-4xl font-extrabold uppercase text-foreground md:text-6xl">Command Center</h1>
          </div>
          <Button variant="outline" onClick={logout} data-testid="admin-logout" className="rounded-none border-border font-mono text-xs uppercase tracking-widest hover:border-destructive hover:text-destructive">
            <LogOut className="mr-2 h-4 w-4" /> Logout
          </Button>
        </div>

        <div className="mt-10 flex flex-wrap gap-px border border-border bg-border" data-testid="admin-tabs">
          {TABS.map((t) => (
            <button key={t} onClick={() => setTab(t)} data-testid={`admin-tab-${t.toLowerCase()}`}
              className={`px-6 py-4 font-mono text-xs uppercase tracking-widest transition-colors
                ${tab === t ? "bg-cyan text-white" : "bg-background text-muted-foreground hover:bg-card hover:text-foreground"}`}>
              {t}
            </button>
          ))}
        </div>

        <div className="mt-8">
          {tab === "Leads" && <AdminLeads />}
          {tab === "Queries" && <AdminQueries />}
          {tab === "Journal" && <AdminBlogs />}
          {tab === "FAQs" && <AdminFaqs />}
          {tab === "Quotes" && <AdminQuotes />}
        </div>
      </div>
    </div>
  );
}
